import { Container, Box, IconButton, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import { useCallback, useEffect, useState } from "react";
import { DashboardHeader, StatsTilesRow, RevenueChart, OrderStatusCard } from "./components";
import { Edit, Lock, Refresh } from "@mui/icons-material";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { userStore } from "../../../../app/stores/User";
import { dashboardStore } from "../../../../app/stores/Dashboard";
import { DashboardService } from "../../../../services/supabase/DashboardService";
import analyticsService, { AnalyticsSummary } from "../../../../services/analytics/AnalyticsService";
import VisitorStatsCard from "./components/VisitorStatsCard";
import RecentActivityCard from "./components/RecentActivityCard";
import { Responsive, WidthProvider, Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './Dashboard.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

// Default layout configuration for different breakpoints
// minW must not exceed the smallest breakpoint cols (xxs: 2)
const defaultLayouts = {
    lg: [
        { i: 'visitors', x: 0, y: 0, w: 4, h: 4, minW: 2, minH: 3, maxH: 8 },
        { i: 'activity', x: 4, y: 0, w: 4, h: 4, minW: 2, minH: 3, maxH: 8 },
        { i: 'orders', x: 8, y: 0, w: 4, h: 9, minW: 2, minH: 4, maxH: 12 },
        { i: 'revenue', x: 0, y: 4, w: 8, h: 5, minW: 2, minH: 4, maxH: 8 },
    ],
    md: [
        { i: 'visitors', x: 0, y: 0, w: 5, h: 4, minW: 2, minH: 3, maxH: 8 },
        { i: 'activity', x: 5, y: 0, w: 5, h: 4, minW: 2, minH: 3, maxH: 8 },
        { i: 'orders', x: 0, y: 4, w: 5, h: 6, minW: 2, minH: 4, maxH: 12 },
        { i: 'revenue', x: 5, y: 4, w: 5, h: 6, minW: 2, minH: 4, maxH: 8 },
    ],
    sm: [
        { i: 'visitors', x: 0, y: 0, w: 3, h: 4, minW: 2, minH: 3, maxH: 8 },
        { i: 'activity', x: 3, y: 0, w: 3, h: 4, minW: 2, minH: 3, maxH: 8 },
        { i: 'orders', x: 0, y: 4, w: 6, h: 5, minW: 2, minH: 4, maxH: 12 },
        { i: 'revenue', x: 0, y: 9, w: 6, h: 5, minW: 2, minH: 4, maxH: 8 },
    ],
    xs: [
        { i: 'visitors', x: 0, y: 0, w: 2, h: 5, minW: 2, minH: 3, maxH: 8 },
        { i: 'activity', x: 2, y: 0, w: 2, h: 5, minW: 2, minH: 3, maxH: 8 },
        { i: 'orders', x: 0, y: 5, w: 4, h: 6, minW: 2, minH: 4, maxH: 12 },
        { i: 'revenue', x: 0, y: 11, w: 4, h: 6, minW: 2, minH: 4, maxH: 8 },
    ],
    xxs: [
        { i: 'visitors', x: 0, y: 0, w: 2, h: 5, minW: 2, minH: 3, maxH: 8 },
        { i: 'activity', x: 0, y: 5, w: 2, h: 5, minW: 2, minH: 3, maxH: 8 },
        { i: 'orders', x: 0, y: 10, w: 2, h: 6, minW: 2, minH: 4, maxH: 12 },
        { i: 'revenue', x: 0, y: 16, w: 2, h: 6, minW: 2, minH: 4, maxH: 8 },
    ],
};

const Dashboard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { user } = userStore((state) => state);
    const { selectedDateRange } = dashboardStore((state) => state);
    const dashboardService = DashboardService();
    const [analyticsSummary, setAnalyticsSummary] = useState<AnalyticsSummary | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [layouts, setLayouts] = useState(defaultLayouts);

    // Load saved layout from localStorage
    useEffect(() => {
        const savedLayout = localStorage.getItem('dashboardLayout');
        if (savedLayout) {
            try {
                const parsed = JSON.parse(savedLayout);
                // Check if it's the old single-layout format and migrate
                if (parsed.lg && !parsed.md) {
                    // Old format - reset to new default layouts
                    setLayouts(defaultLayouts);
                    localStorage.setItem('dashboardLayout', JSON.stringify(defaultLayouts));
                } else {
                    // Validate and fix minW values to prevent react-grid-layout warnings
                    const fixedLayouts: typeof defaultLayouts = { lg: [], md: [], sm: [], xs: [], xxs: [] };
                    for (const [breakpoint, layout] of Object.entries(parsed)) {
                        if (Array.isArray(layout)) {
                            fixedLayouts[breakpoint as keyof typeof defaultLayouts] = layout.map((item: Layout) => ({
                                ...item,
                                minW: Math.min(item.minW || 2, 2)
                            }));
                        }
                    }
                    setLayouts(fixedLayouts);
                    localStorage.setItem('dashboardLayout', JSON.stringify(fixedLayouts));
                }
            } catch (e) {
                console.error('Failed to load saved layout:', e);
                setLayouts(defaultLayouts);
            }
        } else {
            setLayouts(defaultLayouts);
            localStorage.setItem('dashboardLayout', JSON.stringify(defaultLayouts));
        }
    }, []);

    // Fetch dashboard data on component mount
    useEffect(() => {
        const fetchData = async () => {
            const startDate = moment().subtract(1, 'month').format("YYYY-MM-DD");
            const endDate = moment().format("YYYY-MM-DD");
            await dashboardService.getDashboardData(
                startDate,
                endDate
            );

            // Fetch analytics summary for visitor stats
            const analyticsResult = await analyticsService.getAnalyticsSummary();
            if (analyticsResult.success && analyticsResult.data) {
                setAnalyticsSummary(analyticsResult.data);
            }
        };
        fetchData();
    }, []);

    const handleDateChange = useCallback((date: Date) => {
        const startDate = moment(date).startOf('month').format("YYYY-MM-DD");
        const endDate = moment(date).endOf('month').format("YYYY-MM-DD");

        // Update the date range and refetch data
        dashboardStore.getState().setSelectedDateRange(startDate, endDate);
        dashboardService.getDashboardData(startDate, endDate);
    }, [dashboardService]);

    const handleLayoutChange = (_layout: Layout[], allLayouts: typeof defaultLayouts) => {
        if (isEditMode) {
            setLayouts(allLayouts);
            localStorage.setItem('dashboardLayout', JSON.stringify(allLayouts));
        }
    };

    const toggleEditMode = () => {
        setIsEditMode(!isEditMode);
    };

    const resetLayout = () => {
        setLayouts(defaultLayouts);
        localStorage.setItem('dashboardLayout', JSON.stringify(defaultLayouts));
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Container
                component="main"
                maxWidth="xl"
                sx={{
                    height: "100%",
                    paddingX: { xs: 0, md: 2 },
                }}>

                <Box sx={{ width: "100%" }}>
                    {/* Dashboard Header with Welcome message and Controls */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <DashboardHeader
                            userName={user.info.user_metadata.firstName}
                            onDateChange={handleDateChange}
                            initialDate={moment(selectedDateRange.endDate).toDate()}
                        />

                        {/* Layout Controls */}
                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                            {isEditMode && (
                                <Tooltip title='Reset to Default Layout'>
                                    <IconButton
                                        onClick={resetLayout}
                                        sx={{
                                            backgroundColor: 'secondary.main',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'secondary.dark',
                                            },
                                        }}
                                        size='small'
                                    >
                                        <Refresh fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title={isEditMode ? 'Lock Layout' : 'Customize Dashboard'}>
                                <IconButton
                                    onClick={toggleEditMode}
                                    sx={{
                                        backgroundColor: isEditMode ? 'warning.main' : 'primary.main',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: isEditMode ? 'warning.dark' : 'primary.dark',
                                        },
                                    }}
                                    size='small'
                                >
                                    {isEditMode ? <Lock fontSize='small' /> : <Edit fontSize='small' />}
                                </IconButton>
                            </Tooltip>

                        </Box>
                    </Box>

                    {/* Statistics Tiles Row */}
                    <StatsTilesRow analyticsSummary={analyticsSummary} />

                    {/* Customizable Grid Layout */}
                    <ResponsiveGridLayout
                        className='layout'
                        layouts={layouts}
                        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                        cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                        rowHeight={isMobile ? 60 : 80}
                        onLayoutChange={handleLayoutChange}
                        isDraggable={isEditMode}
                        isResizable={isEditMode}
                        compactType='vertical'
                        preventCollision={false}
                        margin={isMobile ? [12, 12] : [16, 16]}
                        containerPadding={[0, 0]}
                        draggableHandle='.drag-handle'
                        resizeHandles={['se']}
                    >
                        <Box key='visitors' sx={{
                            height: '100%',
                            border: isEditMode ? '2px dashed rgba(128, 0, 128, 0.3)' : 'none',
                            borderRadius: 2,
                            position: 'relative',
                        }}>
                            <Box
                                className={isEditMode ? 'drag-handle' : ''}
                                sx={{
                                    height: '100%',
                                    cursor: isEditMode ? 'move' : 'default',
                                    overflow: 'visible', // Changed from 'auto' to prevent shadow clipping
                                }}
                            >
                                <VisitorStatsCard summary={analyticsSummary} />
                            </Box>
                        </Box>

                        <Box key='activity' sx={{
                            height: '100%',
                            border: isEditMode ? '2px dashed rgba(128, 0, 128, 0.3)' : 'none',
                            borderRadius: 2,
                            position: 'relative',
                        }}>
                            <Box
                                className={isEditMode ? 'drag-handle' : ''}
                                sx={{
                                    height: '100%',
                                    cursor: isEditMode ? 'move' : 'default',
                                    overflow: 'visible',
                                }}
                            >
                                <RecentActivityCard />
                            </Box>
                        </Box>

                        <Box key='orders' sx={{
                            height: '100%',
                            border: isEditMode ? '2px dashed rgba(128, 0, 128, 0.3)' : 'none',
                            borderRadius: 2,
                            position: 'relative',
                        }}>
                            <Box
                                className={isEditMode ? 'drag-handle' : ''}
                                sx={{
                                    height: '100%',
                                    cursor: isEditMode ? 'move' : 'default',
                                    overflow: 'visible',
                                }}
                            >
                                <OrderStatusCard title='Order Status Distribution' />
                            </Box>
                        </Box>

                        <Box key='revenue' sx={{
                            height: '100%',
                            border: isEditMode ? '2px dashed rgba(128, 0, 128, 0.3)' : 'none',
                            borderRadius: 2,
                            position: 'relative',
                        }}>
                            <Box
                                className={isEditMode ? 'drag-handle' : ''}
                                sx={{
                                    height: '100%',
                                    cursor: isEditMode ? 'move' : 'default',
                                    overflow: 'visible',
                                }}
                            >
                                <RevenueChart title='Revenue vs Order' />
                            </Box>
                        </Box>
                    </ResponsiveGridLayout>
                </Box>

            </Container>
        </LocalizationProvider>
    );
};

export default Dashboard;
